import React, { useEffect, useState } from 'react';
import styles from './NewPage.module.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InternalHeader from '../../components/InternalHeader/InternalHeader';
import { Helmet } from 'react-helmet';
import api from '../../utils/api';
import { transformNewsArticle } from '../../utils/transformers';
import { NewsData } from '../../components/NewsCard/NewsCard';
import cn from 'classnames';

export default function NewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<NewsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadNewsData = async () => {
      if (location.state?.news) {
        setNews(location.state.news);
        return;
      }

      if (!id) {
        navigate('/news');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const articleResponse = await api.news.getById(parseInt(id));
        const transformedArticle = transformNewsArticle(articleResponse);
        
        setNews(transformedArticle);
      } catch (err) {
        console.error('Error fetching news article:', err);
        setError('Не удалось загрузить новость');
        
        setTimeout(() => {
          navigate('/news');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    loadNewsData();
  }, [location.state, navigate, id]);

  const handleClose = () => {
    navigate(-1);
  };

  const renderText = (text: string | undefined) => {
    if (!text) return null;
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  const renderContent = (content: NewsData['content']) => {
    if (!content || !Array.isArray(content)) {
      return <p>Контент недоступен.</p>;
    }

    return content.map((item, index) => {
      switch (item.type) {
        case 'paragraph':
          return <p key={index}>{renderText(item.text)}</p>;
        case 'subtitle':
          return (
            <h4 key={index} className={styles.subtitle}>
              {renderText(item.text)}
            </h4>
          );
        case 'list':
          return (
            <ul key={index}>
              {item.items?.map((listItem, idx) => (
                <li key={idx} className={styles.li}>{renderText(listItem)}</li>
              ))}
            </ul>
          );
        case 'video':
          return (
            <div key={index} className={styles.video_wrapper}>
              <iframe
                src={item.src}
                width="100%"
                height={480}
                style={{ backgroundColor: '#000', border: 0 }}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
                frameBorder={0}
                allowFullScreen
                title={`vk-video-${index}`}
              />
              {item.caption && (
                <p className={cn('text_body_b3', styles.caption)}>
                  {renderText(item.caption)}
                </p>
              )}
            </div>
          );
        case 'image':
          return (
            <div key={index}>
              <img className={styles.inner_image} src={item.src} alt={news?.title} />
              {item.caption && (
                <p className={cn('text_body_b3', styles.caption)}>
                  {renderText(item.caption)}
                </p>
              )}
            </div>
          );
        default:
          return null;
      }
    });
  };

  if (loading) {
    return (
      <div className={styles.news_page_wrapper}>
        <div className={styles.wrapper}>
          <div className={styles.loading}>Загрузка...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.news_page_wrapper}>
        <div className={styles.wrapper}>
          <div className={styles.error}>{error}</div>
        </div>
      </div>
    );
  }

  if (!news) {
    return <div>Новость не найдена</div>;
  }

  return (
    <>
      <Helmet>
        <title>{news.title} | Восток Строй</title>
        <meta name="description" content={news.shortDescription || news.title} />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={news.title} />
        <meta property="og:description" content={news.shortDescription || news.title} />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Восток Строй" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={news.title} />
        <meta name="twitter:description" content={news.shortDescription || news.title} />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      <div className={styles.news_page_wrapper}>
        <InternalHeader title={news.title} onClose={handleClose} />
        <div className={styles.wrapper}>
          <div className={styles.title}>
            <p className={cn('text-2_font', styles.date)}>{news.date}</p>
            <h2 className={styles.header}>{renderText(news.title)}</h2>
          </div>
          <div className={cn('text-2_font', styles.content)}>
            {renderContent(news.content)}
          </div>
        </div>
      </div>
    </>
  );
}